import { useState } from 'react';

import { IconArrowLeft, IconUserMinus } from '@tabler/icons-react';
import { AgGridReact } from 'ag-grid-react';
import { omit } from 'lodash';

import { Box, Dialog, IconButton, Typography } from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';

import { ContractSummary } from '@octopus/api';
import { agGridThemes } from '@octopus/ui/shared-components';

import {
  formInputKeys,
  terminationReason,
  typeOfNotice,
} from '../../constants';
import { useFormInput } from '../../hooks/useFormInput';
import {
  toTerminationInput,
  useGenerateSimulation,
} from '../../hooks/useGenerateSimulation';
import {
  ContractSummaryWithFgtsBalance,
  TerminationSimulationInputs,
} from '../../types';
import {
  defaultColDef,
  getFormColumns,
  selectionColumnDef,
} from '../table/Columns';

import { EmployeeInfo } from './EmployeeInfo';
import { FormActionBar } from './FormActionBar';
import {
  DatePickerInput,
  NoticeDaysInput,
  SelectInput,
  TextFieldInput,
  shouldShowInput,
} from './Inputs';

export const TerminationSimulationForm = ({
  open,
  onClose,
  selectedContracts,
}: {
  open: boolean;
  onClose: () => void;
  selectedContracts: ContractSummaryWithFgtsBalance[];
}) => {
  const { form, getInputProps } = useFormInput();
  const { mutate: generateSimulation, isLoading } = useGenerateSimulation({
    onSuccess: () => {
      onClose();
    },
  });
  const [errors, setErrors] = useState<Record<string, boolean>>({});
  const [hasSubmitted, setHasSubmitted] = useState(false);

  const validateForm = () => {
    const newErrors: Record<string, boolean> = {};
    if (!form.reason) newErrors[formInputKeys.reason] = true;
    if (!form.terminationDate) newErrors[formInputKeys.terminationDate] = true;
    if (form.reason) {
      const inputs = omit(formInputKeys, ['reason', 'identifier']);
      Object.keys(inputs).forEach((key) => {
        if (
          shouldShowInput(
            form.reason,
            key as keyof TerminationSimulationInputs,
          ) &&
          !form[key as keyof TerminationSimulationInputs]
        ) {
          newErrors[key] = true;
        }
      });
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSimulate = () => {
    setHasSubmitted(true);

    if (validateForm()) {
      const payload = toTerminationInput(form, selectedContracts);
      console.log({ payload });
      generateSimulation(payload);
    }
  };

  const hasError = (field: string) => hasSubmitted && errors[field];
  const helperText = (field: string) =>
    hasError(field) ? 'Campo obrigatório' : '';

  return (
    <Dialog open={open} PaperProps={{ sx: { borderRadius: 0 } }} fullScreen>
      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
        <Box pb={7} display="flex" height="100%" flexDirection="row">
          <Box width="50%">
            <Box display="flex" alignItems="center" m={2}>
              <IconButton
                size="small"
                onClick={onClose}
                edge="start"
                sx={{
                  '&:hover': {
                    backgroundColor: 'transparent',
                  },
                }}
              >
                <IconArrowLeft size={16} />
                <Typography fontSize={14} fontWeight={700}>
                  Voltar
                </Typography>
              </IconButton>
            </Box>

            <Box pt={7} px={7} display="flex" alignItems="center" mb={3}>
              <IconUserMinus size={24} />
              <Typography ml={1} variant="h5">
                Simular rescisão
              </Typography>
            </Box>

            <Box
              px={7}
              component="form"
              display="flex"
              flexDirection="column"
              gap={3}
            >
              {selectedContracts.length > 1 && (
                <TextFieldInput
                  inputKey={formInputKeys.identifier}
                  getInputProps={getInputProps}
                  label="Identificador"
                  placeholder="Digite o identificador"
                  error={hasError(formInputKeys.identifier)}
                  helperText={helperText(formInputKeys.identifier)}
                />
              )}

              <SelectInput
                getInputProps={getInputProps}
                inputKey={formInputKeys.reason}
                label="Motivo do desligamento"
                placeholder="Selecione o motivo de desligamento"
                options={terminationReason}
                error={hasError(formInputKeys.reason)}
                helperText={helperText(formInputKeys.reason)}
              />

              <DatePickerInput
                label="Data do desligamento"
                inputKey={formInputKeys.terminationDate}
                getInputProps={getInputProps}
                error={hasError(formInputKeys.terminationDate)}
                helperText={helperText(formInputKeys.terminationDate)}
              />

              {shouldShowInput(form.reason, formInputKeys.noticeType) && (
                <SelectInput
                  getInputProps={getInputProps}
                  inputKey={formInputKeys.noticeType}
                  label="Tipo de aviso prévio"
                  placeholder="Selecione o tipo de aviso prévio"
                  options={typeOfNotice}
                  error={hasError(formInputKeys.noticeType)}
                  helperText={helperText(formInputKeys.noticeType)}
                />
              )}

              {shouldShowInput(form.reason, formInputKeys.noticeStartDate) && (
                <Box display="flex" gap={3}>
                  <Box flex="3">
                    <DatePickerInput
                      label="Data de início do aviso prévio"
                      inputKey={formInputKeys.noticeStartDate}
                      getInputProps={getInputProps}
                      error={hasError(formInputKeys.noticeStartDate)}
                      helperText={helperText(formInputKeys.noticeStartDate)}
                    />
                  </Box>

                  <Box flex="1">
                    <NoticeDaysInput
                      getInputProps={getInputProps}
                      label="Dias de aviso"
                      placeholder="00"
                    />
                  </Box>
                </Box>
              )}

              {shouldShowInput(form.reason, formInputKeys.fgtsBalance) && (
                <TextFieldInput
                  inputKey={formInputKeys.fgtsBalance}
                  getInputProps={getInputProps}
                  label="Saldo FGTS"
                  placeholder="R$"
                  error={hasError(formInputKeys.fgtsBalance)}
                  helperText={helperText(formInputKeys.fgtsBalance)}
                />
              )}

              {shouldShowInput(form.reason, formInputKeys.paymentDate) && (
                <DatePickerInput
                  label="Data de pagamento"
                  inputKey={formInputKeys.paymentDate}
                  getInputProps={getInputProps}
                  error={hasError(formInputKeys.paymentDate)}
                  helperText={helperText(formInputKeys.paymentDate)}
                />
              )}
            </Box>
          </Box>

          <Box p={7} bgcolor="#F7F7F8" width="50%">
            {selectedContracts.length > 1 ? (
              <AgGridReact<ContractSummary>
                theme={agGridThemes.auditing}
                rowData={selectedContracts}
                domLayout="autoHeight"
                defaultColDef={defaultColDef}
                selectionColumnDef={selectionColumnDef}
                suppressMultiSort
                suppressCellFocus
                suppressMovableColumns
                columnDefs={getFormColumns()}
              />
            ) : (
              <EmployeeInfo
                selectedContracts={selectedContracts}
                terminationDate={form.terminationDate}
              />
            )}
          </Box>
        </Box>
      </LocalizationProvider>
      <FormActionBar onSimulate={handleSimulate} isLoading={isLoading} />
    </Dialog>
  );
};
